import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { useInputId } from './hooks';

function FormRadio({
  id: defaultId,
  icon: Icon,
  label,
  name,
  onChange,
  onSetFieldValue,
  value,
  error,
  required,
  ...props
}) {
  const [id] = useInputId(defaultId);
  const handleChange = ({ target }) => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: target.value,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, target.value);
    }
  };
  const showIcon = Icon || error;
  const isError = !!error;
  return (
    <div
      className={cn(
        'bg-white flex relative z-[1]',
        isError
          ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
          : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
      )}
    >
      <label htmlFor={id} className="block text-sm font-medium py-2">
        <input
          id={id}
          type="radio"
          name="name"
          className="h-4 w-4 text-primary-800 border-gray-300"
          onChange={handleChange}
          value={value}
          required={required}
          {...props}
        />
        <span className="ml-2">{label}</span>
        {required && <span className="ml-1 font-light text-red-500">*</span>}
      </label>
      {showIcon && (
        <div
          className="pl-1 flex items-center pointer-events-none"
          title={error || ''}
        >
          {error ? (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          ) : (
            <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  );
}

FormRadio.defaultProps = {
  id: '',
  icon: false,
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
};

FormRadio.propTypes = {
  id: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default React.memo(FormRadio);
